/**
 * 分级缓存服务
 * 功能：
 * 1. 按用户维度缓存权限数据
 * 2. 提供精准失效机制
 * 3. 支持缓存过期时间设置
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // 生存时间（毫秒）
}

interface UserPermissionCache {
  userId: string;
  roleId?: string;
  menus?: any[];
  actions?: any[];
  roles?: any[];
  timestamp: number;
  ttl: number;
}

/**
 * 缓存存储类
 */
class PermissionCacheStore {
  private static readonly CACHE_PREFIX = 'permission_cache_';
  private static readonly INDEX_KEY = 'permission_cache_index';
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5分钟
  private static readonly MAX_CACHE_ENTRIES = 100;

  /**
   * 生成用户缓存key
   */
  private static getCacheKey(userId: string, type: string): string {
    return `${this.CACHE_PREFIX}${userId}_${type}`;
  }

  /**
   * 获取缓存索引（跟踪所有用户缓存）
   */
  private static getIndex(): Map<string, Set<string>> {
    try {
      const indexStr = sessionStorage.getItem(this.INDEX_KEY);
      if (!indexStr) return new Map();
      
      const indexObj = JSON.parse(indexStr);
      const map = new Map<string, Set<string>>();
      
      Object.keys(indexObj).forEach(userId => {
        map.set(userId, new Set(indexObj[userId]));
      });
      
      return map;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[Cache] Failed to parse cache index:', error);
      return new Map();
    }
  }

  /**
   * 保存缓存索引
   */
  private static saveIndex(index: Map<string, Set<string>>): void {
    try {
      const indexObj: { [key: string]: string[] } = {};
      index.forEach((keys, userId) => {
        indexObj[userId] = Array.from(keys);
      });
      sessionStorage.setItem(this.INDEX_KEY, JSON.stringify(indexObj));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[Cache] Failed to save cache index:', error);
    }
  }

  /**
   * 检查缓存是否过期
   */
  private static isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  /**
   * 存储缓存数据
   */
  static setCache<T>(userId: string, type: string, data: T, ttl?: number): void {
    try {
      const key = this.getCacheKey(userId, type);
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl: ttl || this.DEFAULT_TTL,
      };

      sessionStorage.setItem(key, JSON.stringify(entry));

      // 更新索引
      const index = this.getIndex();
      if (!index.has(userId)) {
        index.set(userId, new Set());
      }
      index.get(userId)!.add(key);
      this.saveIndex(index);

      // eslint-disable-next-line no-console
      console.log(`[Cache] Set cache for user ${userId} type ${type}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[Cache] Failed to set cache for ${userId}:${type}:`, error);
    }
  }

  /**
   * 获取缓存数据
   */
  static getCache<T>(userId: string, type: string): T | null {
    try {
      const key = this.getCacheKey(userId, type);
      const data = sessionStorage.getItem(key);

      if (!data) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(data);

      if (this.isExpired(entry)) {
        sessionStorage.removeItem(key);
        // eslint-disable-next-line no-console
        console.log(`[Cache] Cache expired for ${userId}:${type}`);
        return null;
      }

      // eslint-disable-next-line no-console
      console.log(`[Cache] Cache hit for user ${userId} type ${type}`);
      return entry.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[Cache] Failed to get cache for ${userId}:${type}:`, error);
      return null;
    }
  }

  /**
   * 清除特定用户的所有缓存
   */
  static clearUserCache(userId: string): void {
    try {
      const index = this.getIndex();
      const keys = index.get(userId);

      if (keys) {
        keys.forEach(key => {
          sessionStorage.removeItem(key);
        });
        index.delete(userId);
        this.saveIndex(index);
      }

      // eslint-disable-next-line no-console
      console.log(`[Cache] Cleared all cache for user ${userId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[Cache] Failed to clear cache for ${userId}:`, error);
    }
  }

  /**
   * 清除特定用户的特定类型缓存
   */
  static clearUserCacheByType(userId: string, type: string): void {
    try {
      const key = this.getCacheKey(userId, type);
      sessionStorage.removeItem(key);

      const index = this.getIndex();
      if (index.has(userId)) {
        index.get(userId)!.delete(key);
        this.saveIndex(index);
      }

      // eslint-disable-next-line no-console
      console.log(`[Cache] Cleared cache for user ${userId} type ${type}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[Cache] Failed to clear cache for ${userId}:${type}:`, error);
    }
  }

  /**
   * 清除指定用户列表的缓存
   * 用于权限变更时精准失效
   */
  static clearUsersCache(userIds: string[]): void {
    try {
      const index = this.getIndex();

      userIds.forEach(userId => {
        const keys = index.get(userId);
        if (keys) {
          keys.forEach(key => {
            sessionStorage.removeItem(key);
          });
          index.delete(userId);
        }
      });

      this.saveIndex(index);
      // eslint-disable-next-line no-console
      console.log(`[Cache] Cleared cache for ${userIds.length} users:`, userIds);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[Cache] Failed to clear cache for users:', error);
    }
  }

  /**
   * 清除指定角色的所有用户缓存
   * 用于角色权限变更时
   */
  static async clearRoleUsersCache(roleId: string): Promise<void> {
    try {
      const response = await fetch(`/api/permissionUsers?roleId=${roleId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('session')}`,
        },
      });

      if (response.ok) {
        const users = await response.json();
        const userIds = users
          .filter((u: any) => u && u.id)
          .map((u: any) => String(u.id));

        this.clearUsersCache(userIds);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Cache] Failed to clear cache for role:', error);
    }
  }

  /**
   * 清除指定菜单相关用户的缓存
   * 用于菜单权限变更时
   */
  static async clearMenuRelatedUsersCache(menuId: string): Promise<void> {
    try {
      // 获取包含该菜单的所有角色
      const response = await fetch(`/api/permissionroles?menuId=${menuId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('session')}`,
        },
      });

      if (response.ok) {
        const roles = await response.json();
        const roleIds = roles
          .filter((r: any) => r && r.id)
          .map((r: any) => String(r.id));

        // 为每个角色清除其用户的缓存
        for (const roleId of roleIds) {
          await this.clearRoleUsersCache(roleId);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Cache] Failed to clear cache for menu:', error);
    }
  }

  /**
   * 清除所有缓存
   */
  static clearAllCache(): void {
    try {
      const index = this.getIndex();
      index.forEach((keys) => {
        keys.forEach(key => {
          sessionStorage.removeItem(key);
        });
      });
      sessionStorage.removeItem(this.INDEX_KEY);

      // eslint-disable-next-line no-console
      console.log('[Cache] Cleared all permission cache');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[Cache] Failed to clear all cache:', error);
    }
  }

  /**
   * 获取缓存统计信息
   */
  static getStats(): { totalUsers: number; totalEntries: number } {
    try {
      const index = this.getIndex();
      let totalEntries = 0;

      index.forEach(keys => {
        totalEntries += keys.size;
      });

      return {
        totalUsers: index.size,
        totalEntries,
      };
    } catch (error) {
      return { totalUsers: 0, totalEntries: 0 };
    }
  }
}

export default PermissionCacheStore;
